import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { IMAGES } from "@/data/content";
import { EVENT_START, getEventState } from "@/lib/eventWindow";
import { EASE } from "@/lib/motion";
import Magnetic from "@/components/Magnetic";

function useCountdown() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const ev = getEventState(now);
  if (ev.phase !== "before") return { phase: ev.phase, day: ev.day };
  const d = EVENT_START - now;
  return {
    phase: "before",
    days: Math.floor(d / 86400000),
    hours: Math.floor((d / 3600000) % 24),
    minutes: Math.floor((d / 60000) % 60),
    seconds: Math.floor((d / 1000) % 60),
  };
}

function Particles() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf, w, h;
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    const P = Array.from({ length: 90 }, () => ({
      x: Math.random(),
      y: Math.random(),
      r: Math.random() * 1.6 + 0.4,
      s: Math.random() * 0.05 + 0.015,
      t: Math.random() * Math.PI * 2,
      tw: Math.random() * 0.02 + 0.008,
    }));
    const resize = () => {
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * DPR;
      canvas.height = h * DPR;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (const p of P) {
        p.y -= p.s / 60;
        if (p.y < -0.05) {
          p.y = 1.05;
          p.x = Math.random();
        }
        p.t += p.tw;
        const a = 0.2 + Math.sin(p.t) * 0.18 + 0.14;
        ctx.beginPath();
        ctx.arc(p.x * w, p.y * h, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(212,175,55,${a})`;
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };
    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) draw();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return <canvas ref={ref} className="absolute inset-0 z-0 h-full w-full" data-testid="hero-particle-canvas" />;
}

const addRipple = (e) => {
  const btn = e.currentTarget;
  const d = Math.max(btn.clientWidth, btn.clientHeight);
  const r = btn.getBoundingClientRect();
  const c = document.createElement("span");
  c.className = "ripple-ink";
  c.style.width = c.style.height = `${d}px`;
  c.style.left = `${e.clientX - r.left - d / 2}px`;
  c.style.top = `${e.clientY - r.top - d / 2}px`;
  btn.appendChild(c);
  setTimeout(() => c.remove(), 700);
};

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

function Digit({ d }) {
  return (
    <span className="relative inline-block h-[1em] w-[0.62em] overflow-hidden" aria-hidden>
      <AnimatePresence initial={false}>
        <motion.span
          key={d}
          initial={{ y: "0.9em", opacity: 0 }}
          animate={{ y: "0em", opacity: 1 }}
          exit={{ y: "-0.9em", opacity: 0 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="absolute inset-0 flex items-center justify-center"
        >
          {d}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

function CountdownTile({ value, label }) {
  const chars = String(value).padStart(2, "0").split("");
  return (
    <div className="glass min-w-[74px] rounded-2xl px-3 py-3 text-center" data-testid={`countdown-${label.toLowerCase()}`}>
      <span className="sr-only">{String(value).padStart(2, "0")}</span>
      <div className="tabular flex justify-center font-display text-2xl font-bold text-gold sm:text-3xl">
        {chars.map((c, i) => (
          <Digit key={i} d={c} />
        ))}
      </div>
      <div className="mt-1 text-[10px] uppercase tracking-[0.25em] text-soft">{label}</div>
    </div>
  );
}

function Medallion({ rx, ry }) {
  return (
    <motion.div
      style={{ rotateX: rx, rotateY: ry, transformPerspective: 1000 }}
      className="relative mx-auto hidden h-[400px] w-[400px] select-none md:block lg:h-[470px] lg:w-[470px]"
      data-testid="hero-medallion"
    >
      <svg viewBox="0 0 200 200" className="spin-slow h-full w-full">
        <defs>
          <path id="ringPath" d="M100,100 m-84,0 a84,84 0 1,1 168,0 a84,84 0 1,1 -168,0" />
        </defs>
        <text fill="#D4AF37" style={{ fontSize: "10.5px", letterSpacing: "3.2px", fontFamily: "'JetBrains Mono', monospace" }}>
          <textPath href="#ringPath">THE DOON SCHOOL • CHANDBAGH • EST. 1935 • DEHRADUN • KNOWLEDGE OUR LIGHT •</textPath>
        </text>
      </svg>
      <div className="glass absolute inset-8 flex flex-col items-center justify-center rounded-full text-center">
        <span className="text-glow font-display text-7xl font-bold text-gold lg:text-8xl">91</span>
        <span className="mt-3 font-mono text-[10px] tracking-[0.4em] text-soft">YEARS OF DOON</span>
        <span className="mt-1 font-serif2 text-lg italic text-fg/80">1935 — 2026</span>
      </div>
      <div className="glass floaty absolute -right-4 top-8 rounded-full px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-fg lg:-right-10">
        Founder's Day • Oct 22–24
      </div>
      <div className="glass floaty floaty-delay absolute -left-2 bottom-14 rounded-full px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-fg lg:-left-8">
        Chandbagh, Dehradun
      </div>
    </motion.div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const cd = useCountdown();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yMedal = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const fade = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const rx = useSpring(useMotionValue(0), { stiffness: 110, damping: 18 });
  const ry = useSpring(useMotionValue(0), { stiffness: 110, damping: 18 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 10);
    rx.set(((e.clientY - r.top) / r.height - 0.5) * -10);
  };

  return (
    <section id="home" ref={ref} onMouseMove={onMove} className="relative flex min-h-screen items-center overflow-hidden pb-24 pt-32">
      <motion.div style={{ y: yBg }} className="pointer-events-none absolute inset-0">
        <img src={IMAGES.hero} alt="" className="h-full w-full object-cover opacity-[0.16]" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/85 to-ink" />
        <div className="drift-slow absolute left-1/2 top-1/3 h-[560px] w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gold/10 blur-[140px]" />
      </motion.div>
      <Particles />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl gap-14 px-6 lg:grid-cols-[1.1fr_.9fr] lg:items-center">
        <div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }} className="inline-flex">
            <span className="glass floaty inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[11px] tracking-wide text-fg sm:text-xs">
              <Sparkles className="h-3.5 w-3.5 shrink-0 text-gold" />
              Celebrating 91 Years of Legacy &amp; Leadership (1935–2026)
            </span>
          </motion.div>

          <h1 className="mt-8 font-display text-[clamp(2.5rem,6.6vw,5.6rem)] font-bold leading-[0.98] text-fg" data-testid="hero-headline">
            <Line delay={0.2}>Ninety-One Years</Line>
            <Line delay={0.34}>
              of Legacy <span className="font-serif2 italic text-gold">&amp;</span>
            </Line>
            <Line delay={0.48}>Leadership</Line>
          </h1>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.0, duration: 0.9 }} className="mt-8 flex items-center gap-4">
            <motion.span initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 1.1, duration: 1.1, ease: [0.22, 1, 0.36, 1] }} className="h-[2px] w-20 origin-left bg-gradient-to-r from-transparent via-gold to-transparent sm:w-28" />
            <span className="text-glow whitespace-nowrap font-mono text-[11px] tracking-[0.4em] text-gold sm:text-xs" data-testid="hero-motto">KNOWLEDGE OUR LIGHT</span>
            <motion.span initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 1.1, duration: 1.1, ease: [0.22, 1, 0.36, 1] }} className="hidden h-[2px] max-w-[80px] flex-1 origin-right bg-gradient-to-r from-transparent via-gold to-transparent sm:block" />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.7 }} className="mt-10 flex flex-wrap items-center gap-4">
            <Magnetic>
              <button
                onClick={(e) => { addRipple(e); scrollToId("#schedule"); }}
                data-testid="hero-cta-schedule"
                className="ripple-host relative overflow-hidden rounded-full bg-gold px-8 py-4 font-display text-sm font-bold tracking-[0.2em] text-navy transition-shadow duration-300 hover:shadow-[0_0_40px_rgba(212,175,55,0.35)]"
              >
                <span className="sheen pointer-events-none absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
                <span className="relative">EXPLORE SCHEDULE</span>
              </button>
            </Magnetic>
            <Magnetic>
              <button
                onClick={(e) => { addRipple(e); scrollToId("#map"); }}
                data-testid="hero-cta-tour"
                className="glass ripple-host rounded-full px-8 py-4 font-display text-sm font-bold tracking-[0.2em] text-fg transition-colors duration-300 hover:border-gold/40 hover:text-gold"
              >
                CHANDBAGH TOUR
              </button>
            </Magnetic>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.4, duration: 0.7 }} className="mt-12">
            <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-soft">
              {cd.phase === "after"
                ? "Founder's Day 2026 • Concluded"
                : cd.phase === "live"
                  ? `Founder's Day is live — Day ${cd.day} of 3`
                  : "Counting down to Founder's Day • Oct 22, 2026"}
            </p>
            <div className="mt-4 flex flex-wrap gap-3" data-testid="hero-countdown">
              {cd.phase !== "before" ? (
                <div className="glass rounded-2xl px-6 py-4 font-display text-sm tracking-widest text-gold sm:text-base">
                  {cd.phase === "live" ? "Celebrations underway at Chandbagh" : "See you for the 92nd in 2027"}
                </div>
              ) : (
                <>
                  <CountdownTile value={cd.days} label="Days" />
                  <CountdownTile value={cd.hours} label="Hours" />
                  <CountdownTile value={cd.minutes} label="Minutes" />
                  <CountdownTile value={cd.seconds} label="Seconds" />
                </>
              )}
            </div>
          </motion.div>
        </div>

        <motion.div style={{ y: yMedal }}>
          <Medallion rx={rx} ry={ry} />
        </motion.div>
      </div>

      <motion.button
        style={{ opacity: fade }}
        onClick={() => scrollToId("#timeline")}
        data-testid="hero-scroll-cue"
        aria-label="Scroll to timeline"
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-soft transition-colors hover:text-gold"
      >
        <ArrowDown className="h-5 w-5 animate-bounce" />
      </motion.button>
    </section>
  );
}
